/**
 * Which grocery category (and so which « rayon ») a food belongs in, read from
 * the bilingual ingredient vocabulary.
 *
 * `categorizeIngredient` used to know only English keywords, matched as
 * substrings. A French list filed oignon, ail, citron and poulet under « Autre »
 * (#772), and English lost too: "champignons" was meat (it contains "ham"),
 * "black pepper", "garlic powder" and "cornstarch" were produce, and "eggs" were
 * other. The vocabulary already knows each of those foods in both languages, so
 * an English and a French line for the same food now land in the same rayon.
 *
 * The vocabulary's categories describe what a food IS (protein, herb, liquid),
 * not where a store shelves it, so the mapping below is not one-to-one:
 * "protein" holds eggs, tofu and lentils as well as meat, and "liquid" holds
 * stock, wine and oat milk.
 */
import { GroceryCategory } from '../types/groceryList';
import { resolveIngredient } from '../utils/resolveIngredient';
import {
  entryFor,
  foldSurface,
  type CanonicalIngredientId,
  type IngredientCategory,
} from '../utils/ingredientVocabulary';

const BY_CATEGORY: Record<IngredientCategory, GroceryCategory> = {
  vegetable: GroceryCategory.Produce,
  fruit: GroceryCategory.Produce,
  aromatic: GroceryCategory.Produce,
  herb: GroceryCategory.Spices, // see herbCategory: a fresh herb is produce
  dairy: GroceryCategory.Dairy,
  protein: GroceryCategory.Meat,
  spice: GroceryCategory.Spices,
  grain: GroceryCategory.Pantry,
  thickener: GroceryCategory.Pantry,
  fat: GroceryCategory.Pantry,
  condiment: GroceryCategory.Pantry,
  acid: GroceryCategory.Pantry,
  sweetener: GroceryCategory.Pantry,
  leavening: GroceryCategory.Pantry,
  nut: GroceryCategory.Pantry,
  seed: GroceryCategory.Pantry,
  chocolate: GroceryCategory.Pantry,
  liquid: GroceryCategory.Pantry,
  other: GroceryCategory.Pantry,
};

/** Foods whose shelf is not the one their vocabulary category implies. */
const BY_ID: Record<CanonicalIngredientId, GroceryCategory> = {
  // "protein" that is not at the meat counter
  egg: GroceryCategory.Dairy,
  'egg-yolk': GroceryCategory.Dairy,
  'egg-white': GroceryCategory.Dairy,
  tofu: GroceryCategory.Dairy,
  tempeh: GroceryCategory.Dairy,
  seitan: GroceryCategory.Dairy,
  'plant-mince': GroceryCategory.Dairy,
  tvp: GroceryCategory.Pantry,
  chickpeas: GroceryCategory.Pantry,
  'black-beans': GroceryCategory.Pantry,
  'white-beans': GroceryCategory.Pantry,
  'kidney-beans': GroceryCategory.Pantry,
  'lima-beans': GroceryCategory.Pantry,
  lentils: GroceryCategory.Pantry,
  aquafaba: GroceryCategory.Pantry,
  edamame: GroceryCategory.Frozen,
  // produce sold in a can or a jar, or dried
  'canned-tomatoes': GroceryCategory.Pantry,
  olives: GroceryCategory.Pantry,
  'hearts-of-palm': GroceryCategory.Pantry,
  applesauce: GroceryCategory.Pantry,
  jackfruit: GroceryCategory.Pantry,
  dates: GroceryCategory.Pantry,
  'dried-figs': GroceryCategory.Pantry,
  prunes: GroceryCategory.Pantry,
  // the bread aisle, where the doughs sit too
  bread: GroceryCategory.Bakery,
  tortilla: GroceryCategory.Bakery,
  dough: GroceryCategory.Bakery,
  'pizza-dough': GroceryCategory.Bakery,
  'puff-pastry': GroceryCategory.Bakery,
  'pie-crust': GroceryCategory.Bakery,
  phyllo: GroceryCategory.Bakery,
  // "liquid" that is drunk, or kept cold
  water: GroceryCategory.Beverages,
  wine: GroceryCategory.Beverages,
  'red-wine': GroceryCategory.Beverages,
  'white-wine': GroceryCategory.Beverages,
  sherry: GroceryCategory.Beverages,
  vermouth: GroceryCategory.Beverages,
  beer: GroceryCategory.Beverages,
  amaretto: GroceryCategory.Beverages,
  'cranberry-juice': GroceryCategory.Beverages,
  'pomegranate-juice': GroceryCategory.Beverages,
  'almond-milk': GroceryCategory.Dairy,
  'oat-milk': GroceryCategory.Dairy,
  'soy-milk': GroceryCategory.Dairy,
};

/** Herbs a recipe means fresh unless it says otherwise. */
const FRESH_BY_DEFAULT = new Set<CanonicalIngredientId>([
  'basil', 'parsley', 'cilantro', 'mint', 'dill', 'chives', 'chervil',
  'culantro', 'sorrel', 'lime-leaves', 'fennel-fronds', 'tarragon',
]);

const DRIED_WORDS = /\b(?:dried|dry|ground|seche|sechee|seches|sechees|moulu|moulue|moulus|moulues)\b/;
const FRESH_WORDS = /\b(?:fresh|frais|fraiche|fraiches|sprigs?|brins?|branches?|bunch(?:es)?|bottes?|bouquets?|poignees?|handfuls?)\b/;

const CANNED = /\b(?:canned|cans?|tinned|boite|boites|conserve|conserves|en conserve)\b/;

/** Citrus is squeezed at home: its juice and zest are bought as fruit. */
const SQUEEZED = new Set<CanonicalIngredientId>(['lemon', 'lime', 'orange']);

function herbCategory(id: CanonicalIngredientId, folded: string): GroceryCategory {
  if (DRIED_WORDS.test(folded)) return GroceryCategory.Spices;
  if (FRESH_WORDS.test(folded) || FRESH_BY_DEFAULT.has(id)) return GroceryCategory.Produce;
  return GroceryCategory.Spices;
}

/**
 * "Red pepper" is a bell pepper, but "a pinch of red pepper", "red pepper to
 * taste" and "dried red peppers" are the spice. The vocabulary cannot tell them
 * apart; how the line measures it can.
 */
const PEPPER_AS_SPICE = /\b(?:pinch|pincee|to taste|au gout|ground|moulu|dried|seches?|crushed|flakes?)\b/;

function categoryForId(id: CanonicalIngredientId, folded: string): GroceryCategory | null {
  const entry = entryFor(id);
  if (!entry) return null;
  if (BY_ID[id]) return BY_ID[id];
  if (id === 'bell-pepper' && PEPPER_AS_SPICE.test(folded)) return GroceryCategory.Spices;

  switch (entry.form) {
    case 'powder':
    case 'flakes':
      return GroceryCategory.Spices;
    case 'paste':
    case 'puree':
    case 'dried':
      return GroceryCategory.Pantry;
    case 'juice':
    case 'zest':
      return entry.base && SQUEEZED.has(entry.base)
        ? GroceryCategory.Produce
        : GroceryCategory.Beverages;
  }

  if (entry.category === 'herb') return herbCategory(id, folded);
  const category = BY_CATEGORY[entry.category];
  // "1 boîte de tomates": the parser takes « boîte » as the unit, so the
  // vocabulary only ever sees the tomatoes.
  return category === GroceryCategory.Produce && CANNED.test(folded) ? GroceryCategory.Pantry : category;
}

/**
 * Words that name the product a flavour sits in front of. They decide the shelf
 * whatever food the vocabulary finds: « huile de basilic » is an oil, not basil,
 * and "sherry vinegar" is a vinegar, not sherry. A line that says it is frozen
 * is bought in the freezer aisle, whatever it is.
 */
const PRODUCT_NOUNS: Array<[RegExp, GroceryCategory]> = [
  [/\b(?:frozen|surgele|surgelee|surgeles|surgelees|congele|congelee|congeles|congelees|ice cream|creme glacee)\b/, GroceryCategory.Frozen],
  [/\b(?:oil|oils|huile|huiles|vinegar|vinaigre|vinaigrette|sauce|sauces|pickles?|cornichons?)\b/, GroceryCategory.Pantry],
  [/\b(?:dill|celery|mustard|caraway|nigella) seeds?\b|\bgraines? (?:de |d )?(?:moutarde|aneth|celeri|carvi|nigelle)\b/, GroceryCategory.Spices],
];

export interface VocabularyCategory {
  category: GroceryCategory;
  /**
   * False when the line carried two or more words the vocabulary did not
   * consume, so the matched word may be a flavour or a modifier ("chocolate
   * cake"). The caller should let a more specific rule speak first.
   */
  confident: boolean;
}

/**
 * The grocery category the vocabulary implies for a name or ingredient line, or
 * null when the vocabulary does not know the food.
 */
export function categoryFromVocabulary(text: string): VocabularyCategory | null {
  const folded = foldSurface(text || '');
  if (!folded) return null;
  for (const [words, category] of PRODUCT_NOUNS) {
    if (words.test(folded)) return { category, confident: true };
  }

  const ref = resolveIngredient(text);
  if (!ref.canonicalId) return null;
  const category = categoryForId(ref.canonicalId, folded);
  return category ? { category, confident: ref.confidence >= 0.65 } : null;
}
